import React, { useState, useEffect } from "react";
import { Menu, X, ChevronRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import RevaLogo from "./Images/1111.png";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: "Home",             path: "/" },
    { label: "About",            path: "/conference" },
    { label: "Research Areas",   path: "/research-areas" },
    { label: "Paper Submission", path: "/paper-submission" },
    { label: "Committee",        path: "/committee" },
    { label: "Speakers",         path: "/speakers" },
    { label: "Contact",          path: "/contact" },
  ];

  return (
    <header className="sticky top-0 z-50 w-full">

      {/* ── Top Announcement Bar ── */}
      <div className="bg-[#1a1a2e] text-white text-[11px] md:text-xs font-medium tracking-wide">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-6 py-[7px] flex flex-col sm:flex-row items-center justify-between gap-1">
          <span className="text-orange-300 font-semibold">
            📅&nbsp; July 31 &amp; August 01, 2026 &nbsp;|&nbsp; REVA University, Bengaluru, India
          </span>
          <span className="text-gray-300 hidden sm:block">
            Abstract Submission Deadline:&nbsp;
            <span className="text-white font-semibold">June 15, 2026</span>
            &nbsp;&nbsp;|&nbsp;&nbsp;
            📧&nbsp;ruceconference@reva.edu.in
          </span>
        </div>
      </div>

      {/* ── Main Navbar ── */}
      <div
        className={`bg-white transition-all duration-300 ${
          scrolled
            ? "shadow-[0_4px_24px_rgba(0,0,0,0.10)]"
            : "border-b border-gray-100"
        }`}
      >
        {/* Full-width, no max-width constraint, minimal side padding */}
        <div className="w-full px-4 lg:px-6">
          <div className="h-[72px] flex items-center justify-between gap-4">

            {/* ── Logo + Conference Identity ── */}
            <Link to="/" className="flex items-center gap-3 flex-shrink-0 group">
              {/* Logo only — no text, no border */}
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-orange-100 scale-125 opacity-0 group-hover:opacity-60 transition-all duration-300" />
                <img
                  src={RevaLogo}
                  alt="REVA University"
                  className="relative h-[158px] w-[168px] object-contain"
                />
              </div>

              {/* SMPACE badge + subtitle */}
              <div className="leading-snug">
                <span className="inline-block bg-[#F58220] text-white text-[10px] font-black tracking-[0.14em] uppercase px-2.5 py-[3px] rounded-sm">
                  SMPACE-2026
                </span>
                <p className="text-[11px] text-gray-500 font-medium mt-[3px] hidden md:block whitespace-nowrap">
                  10th International Conference on Sustainable Civil Engineering
                </p>
              </div>
            </Link>

            {/* ── Desktop Nav — pushed right, tight gaps ── */}
            <nav className="hidden lg:flex items-center gap-0.5 ml-auto">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.label}
                    to={item.path}
                    className={`relative px-3.5 py-2 text-[13px] font-semibold rounded-md transition-all duration-200 whitespace-nowrap ${
                      isActive
                        ? "text-[#F58220] bg-orange-50"
                        : "text-gray-700 hover:text-[#F58220] hover:bg-orange-50"
                    }`}
                  >
                    {item.label}
                    {isActive && (
                      <motion.div
                        layoutId="activeUnderline"
                        className="absolute bottom-[3px] left-3 right-3 h-[2px] bg-[#F58220] rounded-full"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* ── Register Now CTA ── */}
            <div className="hidden lg:flex items-center flex-shrink-0 ml-3">
              <Link
                to="/registration"
                className="group relative overflow-hidden bg-[#F58220] hover:bg-[#d96d12] text-white px-5 py-2.5 rounded-lg text-[13px] font-bold shadow-md hover:shadow-lg hover:shadow-orange-200 transition-all duration-300 flex items-center gap-2"
              >
                <span className="relative z-10">Register Now</span>
                <ChevronRight
                  size={15}
                  className="relative z-10 group-hover:translate-x-0.5 transition-transform duration-200"
                />
                {/* shimmer sweep */}
                <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-500 bg-gradient-to-r from-transparent via-white/25 to-transparent" />
              </Link>
            </div>

            {/* ── Mobile Toggle ── */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg bg-orange-50 text-[#F58220] border border-orange-200 hover:bg-[#F58220] hover:text-white transition-all duration-200"
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={isOpen ? "close" : "open"}
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  {isOpen ? <X size={20} /> : <Menu size={20} />}
                </motion.span>
              </AnimatePresence>
            </button>

          </div>
        </div>
      </div>

      {/* ── Bottom Accent Line ── */}
      <div className="h-[3px] bg-gradient-to-r from-[#F58220] via-orange-300 to-[#F58220]" />

      {/* ── Mobile Dropdown ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden bg-white border-b border-orange-100 shadow-xl"
          >
            <div className="px-4 py-4 space-y-1">

              {/* Mobile conference tag */}
              <div className="mb-3 px-3 py-2 bg-orange-50 rounded-lg border border-orange-100">
                <p className="text-[11px] font-black text-[#F58220] tracking-wider uppercase">
                  SMPACE-2026
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  July 31 – Aug 01, 2026 · REVA University, Bengaluru
                </p>
              </div>

              {navItems.map((item, i) => {
                const isActive = location.pathname === item.path;
                return (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04 }}
                  >
                    <Link
                      to={item.path}
                      onClick={() => setIsOpen(false)}
                      className={`flex items-center justify-between px-4 py-3 rounded-lg text-[13.5px] font-semibold transition-all ${
                        isActive
                          ? "bg-[#F58220] text-white shadow-sm"
                          : "text-gray-700 hover:bg-orange-50 hover:text-[#F58220]"
                      }`}
                    >
                      {item.label}
                      <ChevronRight
                        size={15}
                        className={isActive ? "text-white" : "text-gray-400"}
                      />
                    </Link>
                  </motion.div>
                );
              })}

              {/* Mobile Register CTA */}
              <div className="pt-2">
                <Link
                  to="/registration"
                  onClick={() => setIsOpen(false)}
                  className="flex justify-center items-center gap-2 bg-[#F58220] hover:bg-[#d96d12] text-white px-5 py-3 rounded-lg text-[13.5px] font-bold shadow-md transition-all duration-200 w-full"
                >
                  Register Now
                  <ChevronRight size={15} />
                </Link>
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </header>
  );
}